/**
 * iter87.47 — Rivalries Intel v2 · Slice C (frontend tile).
 *
 * Opened via the TodoChips "Rivalries Intel" chip in the Draft section.
 * Renders Card A (top 5 teams by EV) + Card B (best / trap picks) +
 * an info popout defining Expected Value and the factors that go
 * into it — WITHOUT exposing the underlying model, formula, or
 * weights (operator directive from the scope doc).
 */
import { useState } from "react";
import { X, HelpCircle, ChevronUp, Zap, TrendingUp, AlertTriangle, Bot, FlaskConical } from "lucide-react";
import { nationalFlag } from "@/lib/flags";
import { BentoModal } from "@/components/onui/BentoMotion";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

// The four canonical EV signals we surface to users. Order matches
// the scope doc's factor menu; wording carefully avoids sportsbook
// language ("bet", "wager", "lock", "sharp play" etc.).
const FACTOR_EXPLAINERS = [
    { key: "spread", label: "Current spread", tag: "favorite / underdog",
        text: "The betting line for the game frozen at your pick time." },
    { key: "form", label: "Recent form", tag: "form_hot / form_cold",
        text: "How the team has performed against the spread in their last 5 played fixtures." },
    { key: "venue", label: "Home / road split", tag: "home_edge / road_warrior",
        text: "How well the team has covered when playing at home vs on the road this season." },
    { key: "rest", label: "Rest days", tag: "rested / short_week",
        text: "How many days off the team has had vs the opponent — a bigger gap is a bigger edge." },
    { key: "h2h", label: "Head-to-head", tag: "h2h_edge",
        text: "How the team has fared in the last three meetings against this specific opponent." },
];

const FACTOR_CHIP_TONE = {
    favorite: "border-volt/50 text-volt bg-volt/10",
    underdog: "border-cyber/50 text-cyber bg-cyber/10",
    form_hot: "border-heat/50 text-heat bg-heat/10",
    form_cold: "border-zinc-600 text-zinc-500 bg-zinc-800/40",
    home_edge: "border-volt/40 text-volt bg-volt/5",
    road_warrior: "border-cyber/40 text-cyber bg-cyber/5",
    rested: "border-emerald-500/40 text-emerald-400 bg-emerald-950/30",
    short_week: "border-zinc-600 text-zinc-500 bg-zinc-800/40",
    h2h_edge: "border-purple-500/40 text-purple-400 bg-purple-950/30",
};

function FactorChip({ tag }) {
    const tone = FACTOR_CHIP_TONE[tag] || "border-zinc-600 text-zinc-500 bg-zinc-800/40";
    return (
        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 border rounded ${tone}`}
              data-testid={`intel-factor-${tag}`}>
            {tag.replace(/_/g, " ")}
        </span>
    );
}

function InfoPopout({ open, onClose }) {
    if (!open) return null;
    return (
        <div
            data-testid="intel-info-popout"
            className="brutal-card p-4 border-2 border-volt/40 bg-ink space-y-3 mt-2 mb-3"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-volt">
                        What is Expected Value?
                    </p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed mt-1">
                        Expected Value (EV) is a single number that estimates how many
                        Matchday Boost points a team is likely to earn this week under the
                        <span className="font-bold text-white"> 6 / 2 / 0 / −2 </span>
                        ladder. Higher is better.
                    </p>
                </div>
                <button
                    onClick={onClose}
                    data-testid="intel-info-popout-close"
                    className="shrink-0 -mt-1 -mr-1 p-1 text-zinc-500 hover:text-white"
                    aria-label="Close explainer"
                >
                    <ChevronUp className="w-4 h-4" />
                </button>
            </div>
            <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
                    What goes into EV
                </p>
                <ul className="space-y-1.5">
                    {FACTOR_EXPLAINERS.map(f => (
                        <li key={f.key} className="text-[11px] leading-snug">
                            <span className="font-bold text-white">{f.label}</span>
                            <span className="text-zinc-500"> — {f.text}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <p className="text-[10px] text-zinc-600 leading-snug border-t border-zinc-800 pt-2">
                <span className="font-bold text-zinc-500">What EV is NOT</span> — a
                guarantee. Weekly outcomes swing on injuries, weather, and pure
                variance. Treat this as a starting point, not a lock.
            </p>
        </div>
    );
}

function TopCandidateRow({ cand, rank }) {
    const evTone = cand.ev_score >= 3 ? "text-volt" : cand.ev_score >= 1.5 ? "text-cyber" : "text-zinc-400";
    // iter87.493 (user-reported, "make sure Intels reference things
    // correctly") — `spread_home` is always the HOME team's number.
    // For an away pick, the line relative to the picked team is the
    // NEGATION of that, not the raw value — showing it raw displayed
    // a FAVORITE pick (negative line) as a positive number for away
    // picks, and would've rendered "+-3.0" for an away underdog.
    const pickLine = cand.spread_home != null
        ? (cand.side === "home" ? Number(cand.spread_home) : -Number(cand.spread_home))
        : null;
    return (
        <li
            data-testid={`intel-candidate-row-${rank}`}
            className="flex items-center gap-2 py-2 border-b border-zinc-900 last:border-b-0"
        >
            <span className="text-[10px] font-mono font-bold text-zinc-600 w-5 text-right">
                #{rank}
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">
                    {nationalFlag(cand.team_name)} {cand.team_name}
                </p>
                <p className="text-[10px] text-zinc-500 truncate">
                    vs {cand.opponent_name}
                    {cand.side === "home" ? " (home)" : " (away)"}
                    {pickLine != null && (
                        <> · line {pickLine > 0 ? "+" : ""}{pickLine.toFixed(1)}</>
                    )}
                </p>
                {cand.factors_hit && cand.factors_hit.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cand.factors_hit.map(t => <FactorChip key={t} tag={t} />)}
                    </div>
                )}
            </div>
            <div className={`text-right shrink-0 ${evTone}`}>
                <p className="text-[9px] uppercase tracking-wider font-bold opacity-70">EV</p>
                <p className="text-lg font-mono font-black leading-none">
                    {cand.ev_score >= 0 ? "+" : ""}{cand.ev_score.toFixed(1)}
                </p>
            </div>
        </li>
    );
}

function PickBullet({ p, tone, testIdKind, idx }) {
    return (
        <li className={`text-[11px] leading-snug pl-3 -indent-3 ${tone}`}
            data-testid={`intel-${testIdKind}-pick-${idx}`}>
            <span className="font-bold text-white">{p.team}</span>
            <span className="text-zinc-400"> — {p.why}</span>
        </li>
    );
}

export default function RivalriesIntelModal({ open = true, intel, tournament, onClose }) {
    const [infoOpen, setInfoOpen] = useState(false);
    // iter87.199.16 — New shape reads: top3, sharps, trap_picks. Legacy
    // shape fallback keeps in-flight caches from blanking the modal on
    // deploy day. Once the transition window closes we can drop the
    // top5 read.
    const top3 = intel?.top3?.length ? intel.top3 : (intel?.top5 || []).slice(0, 3);
    // iter87.199.49 — Defensive `.slice(0, 1)` cap. Backend already caps
    // `sharps` / `trap_picks` to 1 at write time (parser + storage), but
    // a legacy per-tournament cache doc predating iter87.199.16 can still
    // be surfaced by the read-side fallback in `rivalries_intel.py:592-600`
    // and used to render 3 rows. Belt-and-suspenders — trust in depth so
    // a stale doc can never leak >1 row into the UI again.
    const sharps = (intel?.sharps || []).slice(0, 1);
    const trapPicks = (intel?.trap_picks || []).slice(0, 1);
    const hasData = top3.length > 0;
    // iter87.199.88 — Prefer the sport-friendly matchday label from the
    // first candidate (`round_label` is API-Sports' native label — e.g.
    // `Regular Season - 2` for EPL, `Week 2` for NFL/CFB — which is what
    // players actually see everywhere else in the app). Fall back to the
    // ISO year-week key only when no picks are surfaced.
    //
    // iter87.199.90 — Different sport APIs return round_label in wildly
    // different shapes (CFBD returns bare digits like `1`; API-Sports
    // returns `Regular Season - 1`). Normalise so the badge always reads
    // as either `Week N` (US football) or `MD N` (soccer) — whichever
    // vocabulary the crew uses in the app for that sport.
    const rawRoundLabel = (top3[0]?.round_label || sharps[0]?.round_label || trapPicks[0]?.round_label || "").trim();
    const sportKey = String(intel?.sport_key || "").toLowerCase();
    const isSoccer = sportKey.startsWith("soccer");
    const unitWord = isSoccer ? "MD" : "Week";
    // iter87.377 (user-reported) — NFL/CFB Playoffs don't have "weeks",
    // they have named rounds (Wild Card, Divisional, Conference
    // Championship, Super Bowl / First Round, Quarterfinal, Semifinal,
    // National Championship). `round_label` for these templates is
    // already stored as `"<Round Name> <N>"` (see
    // `football_playoffs_core.py::_round_label_with_number` — the
    // trailing number is an internal sort key for
    // `matchday_boost.round_number()`, not part of the display name),
    // so strip that suffix instead of running it through the generic
    // "Week N" normalizer below. When no round has any candidates yet
    // (playoffs haven't kicked off), leave the badge blank — the
    // calendar-ISO `week_key` fallback ("2026-W42") is meaningless for
    // a bracket format.
    const isPlayoffs = isFootballPlayoffsTemplate(tournament?.template);
    let normalizedRoundLabel = "";
    if (isPlayoffs) {
        normalizedRoundLabel = rawRoundLabel.replace(/\s+\d+$/, "").trim();
    } else if (rawRoundLabel) {
        // Already-formatted labels (`Week 3`, `Matchday 3`, `MD 3`,
        // `Round 5`, `Game 2`) pass through unchanged.
        if (/^(week|matchday|md|round|game)\s*\d/i.test(rawRoundLabel)) {
            normalizedRoundLabel = rawRoundLabel;
        } else {
            // Extract the trailing number from patterns like
            // `Regular Season - 5` or bare `5` — then prefix with the
            // sport-appropriate unit word.
            const digitsMatch = rawRoundLabel.match(/(\d+)\s*$/);
            normalizedRoundLabel = digitsMatch ? `${unitWord} ${digitsMatch[1]}` : rawRoundLabel;
        }
    }
    const weekBadge = normalizedRoundLabel || (isPlayoffs ? "" : intel?.week_key || "");
    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="rivalries-intel-modal"
            backdropClassName="items-end sm:items-center justify-center sm:p-4"
            panelClassName="brutal-card w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto bg-ink border-2 border-volt shadow-2xl"
        >
                {/* Header — iter87.199.88 promoted the week badge from a
                    9px zinc-500 whisper to a volt pill so players can
                    scan the matchday at a glance. */}
                <div className="sticky top-0 z-10 bg-ink border-b-2 border-zinc-800 px-4 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Zap className="w-4 h-4 text-volt shrink-0" />
                        <h2 className="font-display font-extrabold text-base uppercase tracking-wide truncate">
                            Rivalries Intel
                        </h2>
                        {weekBadge && (
                            <span
                                data-testid="intel-week-badge"
                                className="pill text-[10px] font-black uppercase tracking-wider border-volt/50 text-volt bg-volt/10 shrink-0"
                            >
                                {weekBadge}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            data-testid="intel-info-toggle"
                            onClick={() => setInfoOpen(v => !v)}
                            className="p-1.5 text-zinc-500 hover:text-volt"
                            aria-label="What is Expected Value?"
                        >
                            <HelpCircle className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="intel-close-btn"
                            onClick={onClose}
                            className="p-1.5 text-zinc-500 hover:text-white"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    <InfoPopout open={infoOpen} onClose={() => setInfoOpen(false)} />

                    {!hasData && (
                        <div className="brutal-card p-4 text-center text-xs text-zinc-400 leading-relaxed" data-testid="intel-empty-state">
                            {intel?.eligibility_state === "awaiting_odds" ? (
                                "Games are on the schedule, but the odds aren't posted yet — Rivalries Intel needs a spread to work with. Check back closer to kickoff for the Science says Top 3 picks and the Bot says Sharp Pick + Trap Pick."
                            ) : intel?.next_fixture_at ? (
                                `Next games are outside the current pick window — ${new Date(intel.next_fixture_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })}. Check back the week before kickoff for the Science says Top 3 picks and the Bot says Sharp Pick + Trap Pick.`
                            ) : (
                                "No games on the schedule yet — Rivalries Intel picks up as soon as the next matchup is set. Check back closer to the start of the season for the Science says Top 3 picks and the Bot says Sharp Pick + Trap Pick."
                            )}
                        </div>
                    )}

                    {hasData && (
                        <>
                            {/* Science says… — Top 3 EV (deterministic) */}
                            <section data-testid="intel-card-science">
                                <div className="flex items-center gap-2 mb-1">
                                    <FlaskConical className="w-3.5 h-3.5 text-volt" />
                                    <p className="text-[11px] uppercase tracking-widest font-bold text-white">
                                        Science says…
                                    </p>
                                </div>
                                <p className="text-[10px] text-zinc-500 leading-snug mb-2">
                                    Top 3 picks ranked by likely points earned this week under the
                                    <span className="font-bold text-white"> 6 / 2 / 0 / −2 </span>
                                    Matchday Boost ladder.
                                </p>
                                <ul className="brutal-card divide-y divide-zinc-900 px-3 py-1">
                                    {top3.map((c, i) => (
                                        <TopCandidateRow
                                            key={`${c.team_id}-${c.fixture_id}`}
                                            cand={c} rank={i + 1}
                                        />
                                    ))}
                                </ul>
                            </section>

                            {/* iter87.170 — Paused banner. Site admins
                                can flip Site Admin → LLM Monitor → Pause
                                AI to short-circuit all new generations.
                                Cached content still surfaces above;
                                Bot says gets replaced by the friendly
                                "on a coffee break" copy. */}
                            {intel.llm_paused && intel.paused_copy && (
                                <section
                                    data-testid="intel-paused-banner"
                                    className="brutal-card-light p-3 border-amber-500/40 bg-amber-500/10 space-y-1"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="pill bg-amber-500/20 border-amber-500/60 text-amber-300 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest">
                                            AI paused
                                        </span>
                                    </div>
                                    <p className="text-[12px] font-bold text-white">{intel.paused_copy.headline}</p>
                                    <p className="text-[11px] text-zinc-400 leading-snug">{intel.paused_copy.body}</p>
                                </section>
                            )}

                            {/* Bot says… — 1 Sharp Pick + 1 Trap Pick (LLM, fun tone) */}
                            {!intel.llm_paused && (sharps.length > 0 || trapPicks.length > 0) && (
                                <section data-testid="intel-card-bot" className="space-y-2">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Bot className="w-3.5 h-3.5 text-cyber" />
                                        <p className="text-[11px] uppercase tracking-widest font-bold text-white">
                                            Bot says…
                                        </p>
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-snug mb-2">
                                        {sharps.length > 0 && trapPicks.length > 0 && (
                                            <>The LLM took a look at the week and coughed up a Sharp Pick (the dark horse it's hyped about) and a Trap Pick (the crowd favorite it thinks is a mirage). Take it with a grain of salt — bots may speak out of both sides of their mouth from time to time 🤖</>
                                        )}
                                        {sharps.length > 0 && trapPicks.length === 0 && (
                                            <>The LLM took a look at the week and coughed up a Sharp Pick — the dark horse it's hyped about. Couldn't find a trap it believed in this week. Take with a grain of salt.</>
                                        )}
                                        {sharps.length === 0 && trapPicks.length > 0 && (
                                            <>The LLM took a look at the week and coughed up a Trap Pick — the crowd favorite it thinks is a mirage. Couldn't find a dark horse worth hyping this week. Take with a grain of salt.</>
                                        )}
                                    </p>

                                    {!intel.llm_paused && sharps.length > 0 && (
                                        <div data-testid="intel-bot-sharp">
                                            <div className="flex items-center gap-2 mb-1">
                                                <TrendingUp className="w-3 h-3 text-cyber" />
                                                <p className="text-[10px] uppercase tracking-wider font-bold text-cyber">
                                                    Sharp Pick
                                                </p>
                                            </div>
                                            <ul className="brutal-card p-3 space-y-1.5">
                                                {sharps.map((p, i) => (
                                                    <PickBullet key={`sharp-${i}`} p={p} tone="text-cyber" testIdKind="sharp" idx={i} />
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {!intel.llm_paused && trapPicks.length > 0 && (
                                        <div data-testid="intel-bot-trap">
                                            <div className="flex items-center gap-2 mb-1">
                                                <AlertTriangle className="w-3 h-3 text-heat" />
                                                <p className="text-[10px] uppercase tracking-wider font-bold text-heat">
                                                    Trap Pick
                                                </p>
                                            </div>
                                            <ul className="brutal-card p-3 space-y-1.5">
                                                {trapPicks.map((p, i) => (
                                                    <PickBullet key={`trap-${i}`} p={p} tone="text-heat" testIdKind="trap" idx={i} />
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* iter87.199.173 — Science says can populate on its
                                own (deterministic, no LLM); Bot says needs a
                                successful LLM call. When that call was
                                attempted but came back empty (rate limit, spend
                                cap, transient error) don't just silently drop
                                the section — say so, so it doesn't read as
                                missing copy. */}
                            {!intel.llm_paused && sharps.length === 0 && trapPicks.length === 0 && (
                                <section data-testid="intel-card-bot-unavailable" className="space-y-1.5">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Bot className="w-3.5 h-3.5 text-zinc-500" />
                                        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">
                                            Bot says…
                                        </p>
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-snug">
                                        The bot's taking a breather — no Sharp Pick or Trap Pick this round. Check back in a bit.
                                    </p>
                                </section>
                            )}

                            {/* Attribution + refresh timestamp */}
                            <p className="text-[9px] text-zinc-600 text-center pt-1 border-t border-zinc-900">
                                {intel.llm_source && (
                                    <>Bot says commentary via {intel.llm_source === "anthropic" ? "Claude" : "Claude (Emergent)"} · </>
                                )}
                                Refreshed {intel.generated_at ? new Date(intel.generated_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "recently"}
                            </p>
                        </>
                    )}
                </div>
        </BentoModal>
    );
}
